import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService } from '@quorum/api';
import { environment } from '@quorum/environments';
import { MarketingStoreSettings, User } from '@quorum/models/xs-resource';
import { delay, switchMap, take, tap } from 'rxjs/operators';

@Component({
  selector: 'quorum-marketing-store-settings',
  templateUrl: './marketing-store-settings.component.html',
  styleUrls: ['./marketing-store-settings.component.scss'],
})
export class MarketingStoreSettingsComponent implements OnInit {
  @Input() custId: string;
  settingsForm: FormGroup;
  constructor(
    private apiService: ApiService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private http: HttpClient
  ) {}

  settings: MarketingStoreSettings;
  dealershipUsers: User[];
  uploadedImage: string | null = null;
  selectedFileName: string | null = null;
  selectedFileType: string | null = null;
  selectedFileSize: string | null = null;
  uploadDate: Date | null = null;
  selectedFile: string | null = null;
  file: File | null = null;

  // Per-rooftop logos. The keys come from settings.dealerLogosByRooftop;
  // the migration populated them for every dealer with multi-rooftop disagreement.
  rooftopIds: string[] = [];
  // Tracks which rooftop is mid-upload so the spinner / disabled state shows.
  uploadingRooftopId: string | null = null;
  // Which rooftop matches the dealer-default (used to label the default card).
  primaryRooftopId: string | null = null;
  // "Add another rooftop" form state.
  showAddRooftopForm = false;
  newRooftopId = '';
  newRooftopFile: File | null = null;
  addingRooftop = false;

  ngOnInit(): void {
    this.loadSettings();
  }

  private loadSettings(): void {
    this.apiService
      .get(`/v/1/system/settings/marketing/stores/${this.custId}`)
      .pipe(take(1))
      .subscribe((settings) => {
        this.settings = settings;
        this.settingsForm = this.createSettingsForm(settings);

        this.selectedFileName = settings.dealerLogo?.originalFileName;
        this.uploadedImage = settings.dealerLogo?.url;
        this.uploadDate = settings.dealerLogo?.uploadedAt;
        this.selectedFileSize = settings.dealerLogo?.fileSize;

        // Show only rooftops whose logo overrides the dealer-default. The migration
        // populated every rooftop (including ones that match the default), but
        // displaying the matching ones duplicates the dealer-default card visually.
        // We compare by originalFileName since each upload generates a different
        // KSUID-based s3Key even for the same source file.
        const defaultFileName = settings.dealerLogo?.originalFileName;
        const allRooftops = settings.dealerLogosByRooftop ?? {};
        const sorter = (a: string, b: string) => {
          const an = Number(a);
          const bn = Number(b);
          if (!isNaN(an) && !isNaN(bn)) return an - bn;
          return a.localeCompare(b);
        };
        this.rooftopIds = Object.keys(allRooftops)
          .filter((id) => allRooftops[id]?.originalFileName !== defaultFileName)
          .sort(sorter);

        // Primary rooftop = the rooftopId whose entry matches the dealer-default.
        // Only meaningful when there's at least one rooftop entry — otherwise
        // we don't know which rooftop the default belongs to.
        if (defaultFileName && Object.keys(allRooftops).length > 0) {
          const matchingIds = Object.keys(allRooftops)
            .filter((id) => allRooftops[id]?.originalFileName === defaultFileName)
            .sort(sorter);
          this.primaryRooftopId = matchingIds[0] ?? null;
        } else {
          this.primaryRooftopId = null;
        }

        this.cdr.detectChanges();
      });
  }

  private createSettingsForm(settings: MarketingStoreSettings): FormGroup {
    return this.fb.group({
      PK: [`APPSETTINGS#${this.custId}#MARKETING`],
      SK: ['STORE'],
      twitterHandle: [settings.twitterHandle],
      facebookHandle: [settings.facebookHandle],
      instagramHandle: [settings.instagramHandle],
      websiteUrl: [settings.websiteUrl],
      youtubeHandle: [settings.youtubeHandle],
    });
  }

  updateSettings(settings: MarketingStoreSettings) {
    this.apiService
      .put(`v/1/system/settings/marketing/stores/${this.custId}`, { ...this.settings, ...settings })
      .pipe(take(1))
      .subscribe(() => {
        this.snackBar.open(`Settings saved`, null, { duration: 3000 });
      });
  }

  triggerFileInput() {
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    fileInput.click();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('File exceeds 2MB limit.');
        return;
      }

      this.extractFileMetadata(file);
    }
  }

  private extractFileMetadata(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.uploadedImage = reader.result as string;
      this.selectedFile = e.target.result;
      this.file = file;
      this.selectedFileName = file.name.split('.')[0];
      this.selectedFileType = file.type.split('/')[1];
      this.selectedFileSize = `${Math.round(file.size / 1024)} KB`;
      this.uploadDate = new Date();
      this.cdr.detectChanges();
    };
    reader.readAsDataURL(file);
  }

  removeImage() {
    this.uploadedImage = null;
    this.selectedFileName = null;
    this.selectedFileSize = null;
    this.uploadDate = null;
    this.apiService
      .put(`v/1/system/settings/marketing/stores/${this.custId}`, {
        ...this.settingsForm.value,
      })
      .pipe(
        take(1),
        tap(() => (this.settings = this.settingsForm.value))
      )
      .subscribe();
  }

  updateDealershipLogo() {
    const fileName: string = this.generateId(12) + this.file.name.substring(this.file.name.lastIndexOf('.'));

    const queryParams: any = {
      destinationId: this.custId,
      dateAdded: new Date().toISOString(),
      mediaSource: 'hq',
      dealerLogoOriginalFileName: this.selectedFileName,
      dealerLogoFileSize: this.selectedFileSize,
      dealerLogoUploadDate: this.uploadDate,
    };

    return this.http
      .get(
        `${environment.mediaPresignUrl}/${encodeURIComponent(fileName)}/${encodeURIComponent(this.selectedFileType)}`,
        {
          params: queryParams,
        }
      )
      .pipe(
        switchMap((data: any) => {
          const binary = atob(this.selectedFile.split(',')[1]);
          const array = [];
          for (let i = 0; i < binary.length; i++) {
            array.push(binary.charCodeAt(i));
          }
          const blobData = new Blob([new Uint8Array(array)], { type: this.selectedFileType });

          return this.http.put(data.uploadURL, blobData);
        }),
        delay(1000),
        tap(() => {
          this.snackBar.open(`Dealership letterhead saved`, null, { duration: 3000 });
          this.apiService
            .get(`/v/1/system/settings/marketing/stores/${this.custId}`)
            .pipe(take(1))
            .subscribe((settings) => {
              this.settings = settings;
              this.cdr.detectChanges();
            });
        })
      )
      .subscribe();
  }

  generateId(length: number) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  }

  convertStringCase(str: string) {
    return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
  }

  // ---------- per-rooftop logo handling ----------

  rooftopLogo(rooftopId: string): { url?: string; originalFileName?: string; fileSize?: string; uploadedAt?: string } {
    return this.settings?.dealerLogosByRooftop?.[rooftopId] ?? {};
  }

  triggerRooftopFileInput(rooftopId: string): void {
    const el = document.getElementById(`rooftopFileInput_${rooftopId}`) as HTMLInputElement;
    el?.click();
  }

  onRooftopFileSelected(event: Event, rooftopId: string): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('File exceeds 2MB limit.');
      input.value = '';
      return;
    }
    this.uploadRooftopLogo(rooftopId, file).then(() => {
      input.value = '';
    });
  }

  private async uploadRooftopLogo(rooftopId: string, file: File): Promise<void> {
    this.uploadingRooftopId = rooftopId;
    this.cdr.detectChanges();
    try {
      const buffer = await file.arrayBuffer();
      const blob = new Blob([buffer], { type: file.type || 'application/octet-stream' });
      await this.http
        .post(environment.rooftopLogoUploadUrl, blob, {
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
            'x-destination-id': this.custId,
            'x-rooftop-id': rooftopId,
            'x-original-filename': file.name,
          },
        })
        .pipe(take(1))
        .toPromise();
      this.snackBar.open(`Rooftop ${rooftopId} letterhead updated`, null, { duration: 3000 });
      // Refresh — Lambda has already written DynamoDB by the time this returns.
      this.loadSettings();
    } catch (err: any) {
      console.error('rooftop logo upload failed', err);
      this.snackBar.open(`Upload failed: ${err?.message ?? 'unknown error'}`, null, { duration: 5000 });
    } finally {
      this.uploadingRooftopId = null;
      this.cdr.detectChanges();
    }
  }

  // ---------- add-new-rooftop form ----------

  onNewRooftopFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      this.newRooftopFile = null;
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert('File exceeds 2MB limit.');
      input.value = '';
      this.newRooftopFile = null;
      return;
    }
    this.newRooftopFile = file;
  }

  newRooftopValidationError(): string | null {
    const trimmed = this.newRooftopId.trim();
    if (!trimmed) return null; // empty — show no error, just disable submit
    if (!/^[1-9]\d*$/.test(trimmed)) return 'Rooftop ID must be a positive integer';
    if (this.settings?.dealerLogosByRooftop?.[trimmed]) {
      return `Rooftop ${trimmed} already has a logo — replace it in the list above`;
    }
    return null;
  }

  canSubmitNewRooftop(): boolean {
    return (
      !!this.newRooftopId.trim() &&
      !!this.newRooftopFile &&
      !this.addingRooftop &&
      this.newRooftopValidationError() === null
    );
  }

  async submitNewRooftop(): Promise<void> {
    if (!this.canSubmitNewRooftop() || !this.newRooftopFile) return;
    const rooftopId = this.newRooftopId.trim();
    this.addingRooftop = true;
    this.cdr.detectChanges();
    try {
      await this.uploadRooftopLogo(rooftopId, this.newRooftopFile);
      this.resetAddRooftopForm();
      this.showAddRooftopForm = false;
    } finally {
      this.addingRooftop = false;
      this.cdr.detectChanges();
    }
  }

  openAddRooftopForm(): void {
    this.resetAddRooftopForm();
    this.showAddRooftopForm = true;
  }

  cancelAddRooftop(): void {
    this.resetAddRooftopForm();
    this.showAddRooftopForm = false;
  }

  private resetAddRooftopForm(): void {
    this.newRooftopId = '';
    this.newRooftopFile = null;
    const fileInput = document.getElementById('newRooftopFileInput') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  }
}
